import { capitalize } from "lodash";
import { Locales } from "../locale";

export enum TokenType {
    Pronoun = "PRONOUN", // legacy teen pronoun token
    PartnerPronoun = "PARTNER_PRONOUN",
    SurvivorPronoun = "SURVIVOR_PRONOUN",
    Logic = "LOGIC",
    PartnerOrEx = "PARTNER_OR_EX",
    FriendFamily = "FRIEND_OR_FAMILY",
    Friend = "FRIEND",
    FamilyOrMember = "FAMILY_MEMBER",
    Partner = "PARTNER",
    ExPartner = "EX",
}

export enum NodeType {
    Paragraph = "paragraph",
    Text = "text",
    EmbeddedEntryInline = "embedded-entry-inline",
}

export enum NodeDataType {
    Token = "token",
}

interface NodeData {
    _id?: string;
    _type?: NodeDataType;
    nodeType: NodeType;
    type: TokenType;
    key?: GenderKey | FriendFamilyKey | PartnerKey;
    pronounCapitalized?: boolean;
    text?: TokenText[];
    tokens?: NodeData[];
}

interface TokenText {
    text: string;
    genderKey: GenderKey;
}

interface RichTextNode {
    nodeType: NodeType;
    data: NodeData;
    content?: RichTextNode[];
    value?: string;
}

export enum GenderKey {
    Male = "MALE",
    Female = "FEMALE",
    Nonbinary = "NONBINARY",
}

export enum PartnerKey {
    Partner = "PARTNER",
    ExPartner = "EX",
}

export enum FriendFamilyKey {
    Friend = "FRIEND",
    FamilyMember = "FAMILY_MEMBER",
}

export enum PronounKey {
    They = "THEY",
    Them = "THEM",
    Their = "THEIR",
    Theirs = "THEIRS",
    Themself = "THEMSELF",
    TheyAre = "THEY_ARE",
    TheyArent = "THEY_ARENT",
    TheyWant = "THEY_WANT",
    TheyHave = "THEY_HAVE",
    TheyUse = "THEY_USE",
    TheyDecide = "THEY_DECIDE",
    TheyDont = "THEY_DONT",
    TheyGo = "THEY_GO",
    DoThey = "DO_THEY",
    AreThey = "ARE_THEY",
    HaveThey = "HAVE_THEY",
    TheyTreat = "THEY_TREAT",
}

export const DefaultGenderKeyByLocale = {
    [Locales.EN_US]: GenderKey.Nonbinary,
    [Locales.ES]: GenderKey.Male,
};

const PRONOUN_MAP = {
    [GenderKey.Nonbinary]: {
        [PronounKey.They]: "they",
        [PronounKey.Them]: "them",
        [PronounKey.Their]: "their",
        [PronounKey.Theirs]: "theirs",
        [PronounKey.Themself]: "themself",
        [PronounKey.TheyAre]: "they are",
        [PronounKey.TheyArent]: "they aren't",
        [PronounKey.TheyWant]: "they want",
        [PronounKey.TheyHave]: "they have",
        [PronounKey.TheyUse]: "they use",
        [PronounKey.TheyDecide]: "they decide",
        [PronounKey.TheyDont]: "they don't",
        [PronounKey.TheyGo]: "they go",
        [PronounKey.DoThey]: "do they",
        [PronounKey.AreThey]: "are they",
        [PronounKey.HaveThey]: "have they",
        [PronounKey.TheyTreat]: "they treat",
    },
    [GenderKey.Female]: {
        [PronounKey.They]: "she",
        [PronounKey.Them]: "her",
        [PronounKey.Their]: "her",
        [PronounKey.Theirs]: "hers",
        [PronounKey.Themself]: "herself",
        [PronounKey.TheyAre]: "she is",
        [PronounKey.TheyArent]: "she isn't",
        [PronounKey.TheyWant]: "she wants",
        [PronounKey.TheyHave]: "she has",
        [PronounKey.TheyUse]: "she uses",
        [PronounKey.TheyDecide]: "she decides",
        [PronounKey.TheyDont]: "she doesn't",
        [PronounKey.TheyGo]: "she goes",
        [PronounKey.DoThey]: "does she",
        [PronounKey.AreThey]: "is she",
        [PronounKey.HaveThey]: "has she",
        [PronounKey.TheyTreat]: "she treats",
    },
    [GenderKey.Male]: {
        [PronounKey.They]: "he",
        [PronounKey.Them]: "him",
        [PronounKey.Their]: "his",
        [PronounKey.Theirs]: "his",
        [PronounKey.Themself]: "himself",
        [PronounKey.TheyAre]: "he is",
        [PronounKey.TheyArent]: "he isn't",
        [PronounKey.TheyWant]: "he wants",
        [PronounKey.TheyHave]: "he has",
        [PronounKey.TheyUse]: "he uses",
        [PronounKey.TheyDecide]: "he decides",
        [PronounKey.TheyDont]: "he doesn't",
        [PronounKey.TheyGo]: "he goes",
        [PronounKey.DoThey]: "does he",
        [PronounKey.AreThey]: "is he",
        [PronounKey.HaveThey]: "has he",
        [PronounKey.TheyTreat]: "he treats",
    },
};

/**
 * Replace a token with the correct value based upon
 * gender/pronoun selections in onboarding.
 *
 * Terms:
 *
 * Survivor: Abuse survivor. May be taking the quiz, maybe friend or family of person taking quiz
 * Partner: Partner of survivor
 *
 * partnerGenderKey is most common - represents the partner of survivor.
 * survivorGenderKey is used in the friends/family path
 *
 * Survivor on the friends and family path is not the quiz taker, but a friend or
 * family member of the quiz taker. In these cases we need to identify
 *
 * @param node
 * @param partnerGenderKey
 * @param survivorGenderKey
 * @returns
 */
export const renderEmbededInlineTokenNode = (
    node: RichTextNode,
    partnerGenderKey: GenderKey = GenderKey.Nonbinary,
    survivorGenderKey: GenderKey = GenderKey.Nonbinary,
    friendFamilyKey: TokenType = TokenType.Friend,
    partnerExKey: TokenType = TokenType.Partner,
) => {
    // exit early
    if (node.data._type !== NodeDataType.Token) return null;
    // select gender key for token type
    let _genderKey;
    switch (node.data.type) {
        case TokenType.SurvivorPronoun:
        case TokenType.FriendFamily:
            _genderKey = survivorGenderKey;
            break;
        case TokenType.PartnerPronoun:
        default:
            _genderKey = partnerGenderKey;
            break;
    }

    // handle deprecated, legacy path
    if (!Array.isArray(node.data.text) && !Array.isArray(node.data.tokens)) {
        const pronoun = PRONOUN_MAP[partnerGenderKey]?.[node.data.key] || "";
        return node.data.pronounCapitalized ? capitalize(pronoun) : pronoun;
    }
    if (node.data.type === TokenType.FriendFamily) {
        // handle family / friend
        const token = node.data.tokens?.find(tk => {
            return tk.type === friendFamilyKey;
        });
        const txt = token?.text.find(t => t.genderKey === _genderKey);
        return txt?.text;
    } else if (node.data.type === TokenType.PartnerOrEx) {
        // handle partner / ex
        const token = node.data.tokens?.find(tk => {
            return tk.type === partnerExKey;
        });
        const txt = token?.text.find(t => t.genderKey === _genderKey);
        return txt?.text;
    } else {
        // handle gendered pronoun tokens
        const txt = node.data.text.find(t => t.genderKey === _genderKey);
        return txt?.text;
    }
};
